import { useEffect, useId, useMemo, useState } from "react";
import type { Visibility } from "../../../lib/api/fieldApi";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { Empty, ErrorBanner } from "../../../components/Shell";
import { hasMessage, useT } from "../../../i18n";
import { InvalidField } from "../api/teacherApi";
import type { TeacherApi } from "../api/teacherApi";
import type { CourseSettings, Draft, Recommendation, SaveUi, TeacherStudent } from "../types";
import { NicknameTable } from "./NicknameTable";
import { SegGroup } from "../../../components/ui/Seg";

/**
 * FS-11-05 코스 설정(비동기·공개 범위) — SCR v0.6 sfs-11-05 · SR-TCH-05 · SR-SOC-03 · SR-RUN-06 · SR-SEC-01.
 *  - 작품 공개 범위: 같은 반 전체 / 선택한 학생만 / 비공개 + '선택'일 때 체크 목록 + 비공개 안내
 *  - 코스 프리셋(쉬움·보통·어려움) · 마무리 시간(5·10·15·20분 = 300~1200초) · 추천 모드(원리 태그 → 모드, SR-MODE-08)
 *    · 현실감(센서 노이즈) 끔/켬 · 자동 완료 제안은 PH2(성취 — 흐림)
 *  - 별명표(행마다 바로 저장 — NicknameTable)
 *  - 취소 · 저장(PUT …/teacher/settings) — 422 {field} 는 해당 칸 표시 + 오류 배너
 * 상태: 기본 · 변경됨 · 실명 의심 경고(별명표). 변경은 신규 세션부터(감사 로그 — 서버).
 */
export const PRESET_KEYS = ["easy", "medium", "hard"] as const;
export const TIME_OPTIONS_S = [300, 600, 900, 1200] as const;
const VIS: readonly Visibility[] = ["class", "selected", "private"];

export function isDirty(a: Draft, b: Draft): boolean {
  return (
    a.visibility !== b.visibility ||
    a.presetKey !== b.presetKey ||
    a.timeBudgetS !== b.timeBudgetS ||
    a.realism !== b.realism ||
    (a.visibility === "selected" &&
      [...a.selected].sort().join(",") !== [...b.selected].sort().join(","))
  );
}

export function SettingsTab({
  api,
  workTags,
  onSaveState,
  onSaved,
}: {
  api: TeacherApi;
  /** 코스 작품의 표시 태그(추천 모드 조회) */
  workTags: readonly string[];
  onSaveState: (s: SaveUi) => void;
  onSaved: (s: CourseSettings) => void;
}) {
  const t = useT();
  const id = useId();
  const [server, setServer] = useState<CourseSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [students, setStudents] = useState<TeacherStudent[] | null>(null);
  const [recs, setRecs] = useState<{ tag: string; rec: Recommendation }[]>([]);
  const [saving, setSaving] = useState(false);
  const [invalid, setInvalid] = useState<string | null | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState<string | null>(null);

  useEffect(() => {
    let off = false;
    Promise.all([api.settings(), api.students()])
      .then(([s, st]) => {
        if (off) return;
        setServer(s);
        setDraft(s);
        setStudents(st);
      })
      .catch((e: unknown) => !off && setLoadFailed(e instanceof ApiError ? e.code : "NET-SERVER"));
    return () => {
      off = true;
    };
  }, [api]);

  const tagKey = [...new Set(workTags)].sort().join(",");
  useEffect(() => {
    let off = false;
    // 태그마다 한 번 물어서(원리 태그 ≤6) 가장 무거운 추천 하나 — '기어비 → 오르막 코스'
    const tags = tagKey ? tagKey.split(",").slice(0, 6) : [];
    void Promise.all(
      tags.map(async (tag) => {
        const best = [...(await api.recommend([tag]))].sort((a, b) => b.weight - a.weight)[0];
        return best ? { tag, rec: best } : null;
      }),
    ).then((r) => !off && setRecs(r.filter((x): x is { tag: string; rec: Recommendation } => !!x)));
    return () => {
      off = true;
    };
  }, [api, tagKey]);

  const dirty = !!server && !!draft && isDirty(draft, server);
  const ui: SaveUi = saving ? "saving" : dirty ? "dirty" : "saved";
  useEffect(() => onSaveState(ui), [ui, onSaveState]);

  const withWork = useMemo(() => students ?? [], [students]);

  if (loadFailed)
    return (
      <ErrorBanner
        cause={t("teacher.settings.loadFailed")}
        action={t("teacher.common.retryLater")}
        code={loadFailed}
      />
    );
  if (!draft || !server) return <p className="pf-muted">{t("common.loading")}</p>;

  const put = (p: Partial<Draft>) => {
    setDraft({ ...draft, ...p });
    setInvalid(undefined);
  };

  async function save() {
    if (!draft) return;
    setSaving(true);
    setFailure(null);
    setInvalid(undefined);
    try {
      const s = await api.saveSettings(draft);
      setServer(s);
      setDraft(s);
      onSaved(s);
      setStudents((xs) => xs?.map((x) => ({ ...x, selected: s.selected.includes(x.pid) })) ?? xs);
    } catch (e) {
      if (e instanceof InvalidField) setInvalid(e.field);
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setSaving(false);
    }
  }

  const presetLabel = (k: string) =>
    hasMessage(`course.preset.${k}`) ? t(`course.preset.${k}`) : k;
  const tagLabel = (k: string) => (hasMessage(`student.tags.${k}`) ? t(`student.tags.${k}`) : k);
  const modeLabel = (m: string) => (hasMessage(`teacher.modes.${m}`) ? t(`teacher.modes.${m}`) : m);
  const selectedOn = draft.visibility === "selected";
  const bad = (f: string) => (invalid === f ? true : undefined);

  return (
    <div className="pf-settings" data-testid="settings-tab">
      <div className="pf-settings__top">
        <section className="pf-pane pf-pane--hero" aria-labelledby={`${id}-vis`}>
          <h2 id={`${id}-vis`} className="pf-h3">
            {t("teacher.settings.visTitle")}
          </h2>
          <SegGroup
            label={t("teacher.settings.visTitle")}
            hideLabel
            variant="pill"
            testId="visibility"
            invalid={bad("visibility")}
          >
            {VIS.map((v) => (
              <label key={v} className="pf-seg__opt">
                <input
                  type="radio"
                  name={`${id}-vis`}
                  value={v}
                  checked={draft.visibility === v}
                  onChange={() => put({ visibility: v })}
                />
                <span>{t(`teacher.settings.vis.${v}`)}</span>
              </label>
            ))}
          </SegGroup>
          <p className="pf-note" id={`${id}-sel`}>
            {t("teacher.settings.selectedList")}
          </p>
          {withWork.length === 0 ? (
            <Empty label={t("teacher.settings.noStudents")} />
          ) : (
            <div
              className="pf-checks"
              role="group"
              aria-labelledby={`${id}-sel`}
              aria-disabled={!selectedOn || undefined}
              data-testid="selected-list"
            >
              {withWork.map((s) => (
                <label key={s.pid} className="pf-check">
                  <input
                    type="checkbox"
                    disabled={!selectedOn}
                    checked={draft.selected.includes(s.pid)}
                    data-pid={s.pid}
                    onChange={(e) =>
                      put({
                        selected: e.target.checked
                          ? [...draft.selected, s.pid]
                          : draft.selected.filter((p) => p !== s.pid),
                      })
                    }
                  />
                  {s.nickname}
                </label>
              ))}
            </div>
          )}
          {selectedOn && draft.selected.length === 0 && withWork.length > 0 && (
            <p className="pf-field-err" role="status" data-testid="selected-none">
              {t("teacher.settings.selectedNone")}
            </p>
          )}
          <div className="pf-banner pf-banner--info" role="note">
            <p className="pf-banner__text">{t("teacher.settings.privateNote")}</p>
          </div>
        </section>

        <section className="pf-pane" aria-labelledby={`${id}-pre`}>
          <h2 id={`${id}-pre`} className="pf-h3">
            {t("teacher.settings.presetTitle")}
          </h2>
          <SegGroup label={t("teacher.settings.preset")} testId="preset" invalid={bad("presetKey")}>
            {PRESET_KEYS.map((k) => (
              <label key={k} className="pf-seg__opt">
                <input
                  type="radio"
                  name={`${id}-preset`}
                  value={k}
                  checked={draft.presetKey === k}
                  onChange={() => put({ presetKey: k })}
                />
                <span>{presetLabel(k)}</span>
              </label>
            ))}
          </SegGroup>
          <SegGroup label={t("teacher.settings.time")} testId="time" invalid={bad("timeBudgetS")}>
            {TIME_OPTIONS_S.map((s) => (
              <label key={s} className="pf-seg__opt">
                <input
                  type="radio"
                  name={`${id}-time`}
                  value={s}
                  checked={draft.timeBudgetS === s}
                  onChange={() => put({ timeBudgetS: s })}
                />
                <span>{t("teacher.home.minutes", { n: s / 60 })}</span>
              </label>
            ))}
          </SegGroup>
          {invalid === "timeBudgetS" && (
            <span className="pf-field-err" role="alert">
              {t("teacher.settings.timeInvalid")}
            </span>
          )}
          <div className="pf-field-row" data-testid="recommend">
            <span className="pf-field-label">{t("teacher.settings.recommend")}</span>
            {recs.length === 0 ? (
              <span className="pf-muted">—</span>
            ) : (
              <ul className="pf-chips" style={{ marginTop: 0 }}>
                {recs.map(({ tag, rec }) => (
                  <li key={tag} className="pf-chip-static pf-chip-static--ok" data-tag={tag}>
                    {tagLabel(tag)} → {modeLabel(rec.modeId)}
                    {rec.presetKey ? ` · ${presetLabel(rec.presetKey)}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <SegGroup label={t("teacher.settings.realism")} testId="realism" variant="green">
            {[false, true].map((on) => (
              <label key={String(on)} className="pf-seg__opt">
                <input
                  type="radio"
                  name={`${id}-real`}
                  checked={draft.realism === on}
                  onChange={() => put({ realism: on })}
                />
                <span>{t(on ? "teacher.settings.realismOn" : "teacher.settings.realismOff")}</span>
              </label>
            ))}
          </SegGroup>
          <div className="pf-field-row pf-dimmed" aria-disabled="true" data-phase="2">
            <span className="pf-field-label">{t("teacher.settings.autoComplete")}</span>
            <span className="pf-btn pf-btn--green pf-btn--sm pf-btn--phase2" aria-disabled="true">
              {t("teacher.settings.autoCompletePh2")}
            </span>
          </div>
        </section>
      </div>

      <NicknameTable
        api={api}
        students={students}
        onChange={(s) => setStudents((xs) => xs?.map((x) => (x.pid === s.pid ? s : x)) ?? xs)}
      />

      {invalid !== undefined && (
        <ErrorBanner
          cause={t("teacher.settings.invalid", {
            field: invalid ? t(`teacher.settings.field.${fieldKey(invalid)}`) : "—",
          })}
          action={t("teacher.settings.invalidNext")}
          code="HTTP-422"
        />
      )}
      {failure && (
        <ErrorBanner
          cause={t("teacher.settings.saveFailed")}
          action={t("teacher.common.retryLater")}
          code={failure}
        />
      )}
      <div className="pf-settings__actions">
        <Button
          variant="ghost"
          className="pf-btn--sm"
          disabled={!dirty || saving}
          data-testid="settings-cancel"
          onClick={() => {
            setDraft(server);
            setInvalid(undefined);
          }}
        >
          {t("teacher.common.cancel")}
        </Button>
        <Button
          variant="accent"
          className="pf-btn--sm"
          disabled={!dirty || saving}
          data-testid="settings-save"
          onClick={() => void save()}
        >
          {saving ? t("common.save.saving") : t("teacher.common.save")}
        </Button>
      </div>
      <span className="pf-sr-only" role="status" data-testid="settings-state">
        {t(`teacher.settings.state.${ui}`)}
      </span>
    </div>
  );
}

const FIELDS = ["visibility", "selected", "presetKey", "timeBudgetS", "realism"];
const fieldKey = (f: string) => (FIELDS.includes(f) ? f : "other");
